import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link,
  TextField,
  Button,
  Menu,
  MenuItem,
} from '@mui/material';
import { X, Check, XIcon, Download, History, ChevronLeft, ChevronRight } from 'lucide-react';
import { useSubmissionDetails, useUpdateMetric, useUpdateSubmission } from '@lingaai/shared-hooks';
import { useCurrentUser } from '@lingaai/user-management';
import React, { useState, useEffect } from 'react';
import { DocumentViewerModal } from './DocumentViewerModal';
import { EmailTimelineDrawer } from './EmailTimelineDrawer';
import { ICON_SIZES, ICON_STROKE } from '../constants/icons';
import { downloadCSV, downloadExcel, formatMetricValue } from '@lingaai/shared-utils';

interface SubmissionDetailDrawerProps {
  submissionId: string | null;
  open: boolean;
  onClose: () => void;
  onNavigate?: (submissionId: string) => void;
  submissionIds?: string[];
  fundId?: string;
}

export function SubmissionDetailDrawer({ submissionId, open, onClose, onNavigate, submissionIds = [], fundId }: SubmissionDetailDrawerProps) {
  const { data: submission, isLoading, error } = useSubmissionDetails(submissionId);
  const { data: currentUser } = useCurrentUser();
  const updateMetric = useUpdateMetric();
  const updateSubmission = useUpdateSubmission();
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [highlightMetricId, setHighlightMetricId] = useState<string | null>(null);
  const [editingMetricId, setEditingMetricId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [exportAnchor, setExportAnchor] = useState<null | HTMLElement>(null);
  const [timelineOpen, setTimelineOpen] = useState(false);

  // Navigation logic
  const currentIndex = submissionIds.findIndex((id) => id === submissionId);
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < submissionIds.length - 1;

  const handlePrevious = () => {
    if (hasPrevious && onNavigate) {
      const prevId = submissionIds[currentIndex - 1];
      if (prevId) onNavigate(prevId);
    }
  };

  const handleNext = () => {
    if (hasNext && onNavigate) {
      const nextId = submissionIds[currentIndex + 1];
      if (nextId) onNavigate(nextId);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, hasPrevious, hasNext, currentIndex]);

  const handleViewDocument = (fileId: string | null, sourceType: string) => {
    if (sourceType === 'email_body') {
      setSelectedFileId('email_body');
    } else if (fileId) {
      setSelectedFileId(fileId);
    }
    setViewerOpen(true);
  };

  const [initialPage, setInitialPage] = useState<number | undefined>(undefined);

  const handleViewMetricInDocument = (metric: any) => {
    if (metric.source_file_id && metric.source_coordinates) {
      setSelectedFileId(metric.source_file_id);
      setHighlightMetricId(metric.id);
      setInitialPage(metric.source_page_number || undefined);
      setViewerOpen(true);
    } else if (metric.source_file_id) {
      handleViewDocument(metric.source_file_id, metric.source_type);
    }
  };

  const handleStartEdit = (metricId: string, currentValue: any) => {
    setEditingMetricId(metricId);
    setEditValue(formatValue(currentValue));
  };

  const handleCancelEdit = () => {
    setEditingMetricId(null);
    setEditValue('');
  };

  const handleSaveEdit = async (metricId: string, metric: any) => {
    if (!currentUser) return;

    // Parse the edited value back to the correct format
    let parsedValue: any;
    const metricType = metric.metric_definitions?.data_type;

    if (metricType === 'currency') {
      const numValue = parseFloat(editValue.replace(/[^0-9.-]/g, ''));
      parsedValue = { amount: numValue, currency: 'USD' };
    } else if (metricType === 'percentage') {
      const numValue = parseFloat(editValue.replace('%', ''));
      parsedValue = { percentage: numValue };
    } else if (metricType === 'number') {
      const numValue = parseFloat(editValue.replace(/[^0-9.-]/g, ''));
      parsedValue = { value: numValue };
    } else {
      parsedValue = { value: editValue };
    }

    await updateMetric.mutateAsync({
      metricId,
      updates: {
        value: parsedValue,
        source_type: 'manual',
        extracted_by: 'manual',
        updated_by: currentUser.id,
      },
    });

    setEditingMetricId(null);
    setEditValue('');
  };

  const handleMarkReviewed = async (metricId: string) => {
    if (!currentUser) return;

    await updateMetric.mutateAsync({
      metricId,
      updates: {
        reviewed: true,
        reviewed_by: currentUser.id,
        reviewed_at: new Date().toISOString(),
      },
    });
  };

  const handleMarkSubmissionComplete = async () => {
    if (!currentUser || !submissionId) return;

    await updateSubmission.mutateAsync({
      submissionId,
      updates: {
        status: 'completed',
        updated_by: currentUser.id,
      },
    });
  };

  const handleExport = (format: 'csv' | 'excel') => {
    if (!submission?.metrics || submission.metrics.length === 0) {
      alert('No metrics available to export');
      setExportAnchor(null);
      return;
    }

    const exportData = submission.metrics.map((metric: any) => ({
      Metric: metric.metric_definitions?.name || 'Unknown',
      Value: formatMetricValue(metric.value),
      Confidence: metric.confidence ? `${(metric.confidence * 100).toFixed(0)}%` : '-',
      Source: metric.source_type || '-',
      'Source Location': metric.source_location || '-',
      Reviewed: metric.reviewed ? 'Yes' : 'No',
    }));

    const companyName = submission.portfolio_companies?.name || 'Unknown';
    const filename = `${companyName.replace(/\s+/g, '-')}-${submission.period.replace(' ', '-')}`;

    if (format === 'csv') {
      downloadCSV(exportData, filename);
    } else {
      downloadExcel(exportData, filename);
    }

    setExportAnchor(null);
  };

  const formatValue = (value: any): string => {
    if (!value) return '-';
    // Use the shared formatMetricValue function which handles units
    const formatted = formatMetricValue(value);
    return formatted || '-';
  };

  const statusColors: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'error'> = {
    draft: 'default',
    submitted: 'primary',
    processing: 'warning',
    completed: 'success',
    needs_review: 'error',
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: '100%', sm: 600, md: 800 } },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {onNavigate && submissionIds.length > 1 && (
              <>
                <IconButton onClick={handlePrevious} disabled={!hasPrevious} size="small">
                  <ChevronLeft size={ICON_SIZES.md} strokeWidth={ICON_STROKE} />
                </IconButton>
                <IconButton onClick={handleNext} disabled={!hasNext} size="small">
                  <ChevronRight size={ICON_SIZES.md} strokeWidth={ICON_STROKE} />
                </IconButton>
              </>
            )}
            <Box>
              <Typography variant="h6">Submission Details</Typography>
              {onNavigate && submissionIds.length > 1 && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  Use ← → arrow keys to navigate
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<History size={ICON_SIZES.sm} strokeWidth={ICON_STROKE} />}
              onClick={() => setTimelineOpen(true)}
              sx={{ fontSize: '0.75rem', textTransform: 'none', py: 0.5 }}
            >
              Timeline
            </Button>
            {submission && submission.status === 'completed' && (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Download size={ICON_SIZES.sm} strokeWidth={ICON_STROKE} />}
                  onClick={(e) => setExportAnchor(e.currentTarget)}
                  sx={{ fontSize: '0.75rem', textTransform: 'none', py: 0.5 }}
                >
                  Export
                </Button>
                <Menu anchorEl={exportAnchor} open={Boolean(exportAnchor)} onClose={() => setExportAnchor(null)}>
                  <MenuItem onClick={() => handleExport('csv')}>Export as CSV</MenuItem>
                  <MenuItem onClick={() => handleExport('excel')}>Export as Excel</MenuItem>
                </Menu>
              </>
            )}
            {submission &&
              (submission.status === 'processing' || submission.status === 'needs_review') &&
              (() => {
                // Check if there are any metrics that need review (low confidence and not reviewed)
                const hasMetricsNeedingReview = submission.metrics?.some((metric: any) => {
                  const confidence = metric.confidence ? metric.confidence * 100 : null;
                  return confidence !== null && confidence < 90 && !metric.reviewed;
                });

                return !hasMetricsNeedingReview ? (
                  <Button size="small" variant="contained" onClick={handleMarkSubmissionComplete} disabled={updateSubmission.isPending} sx={{ fontSize: '0.75rem', textTransform: 'none', py: 0.5 }}>
                    Mark Complete
                  </Button>
                ) : null;
              })()}
            <IconButton onClick={onClose} size="small">
              <X size={ICON_SIZES.md} strokeWidth={ICON_STROKE} />
            </IconButton>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {error && <Alert severity="error">{error instanceof Error ? error.message : 'Failed to load submission details'}</Alert>}

          {submission && (
            <>
              {/* Compact Submission Info */}
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'start',
                    mb: 1.5,
                  }}
                >
                  <Box>
                    <Typography variant="h6">{submission.portfolio_companies?.name || 'Unknown'}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {submission.portfolio_companies?.sector || 'No sector'}
                    </Typography>
                  </Box>
                  <Chip label={submission.status} size="small" color={statusColors[submission.status] || 'default'} />
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Period
                    </Typography>
                    <Typography variant="body2">{submission.period}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Source
                    </Typography>
                    <Typography variant="body2">{submission.source_type}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Submitted
                    </Typography>
                    <Typography variant="body2">
                      {submission.submitted_at
                        ? new Date(submission.submitted_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })
                        : '-'}
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              {/* Metrics Table */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1,
                }}
              >
                <Typography variant="subtitle1">Metrics ({submission.metrics?.length || 0})</Typography>
                {submission.status !== 'completed' && (
                  <Typography variant="caption" color="text.secondary">
                    Double-click value to edit
                  </Typography>
                )}
              </Box>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Metric</TableCell>
                      <TableCell>Value</TableCell>
                      <TableCell>Confidence</TableCell>
                      <TableCell>Source</TableCell>
                      <TableCell align="center">Reviewed</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {submission.metrics?.map((metric: any) => {
                      const confidence = metric.confidence ? metric.confidence * 100 : null;
                      const confidenceColor = confidence === null ? 'text.secondary' : confidence >= 90 ? 'success.main' : confidence >= 75 ? 'warning.main' : 'error.main';

                      return (
                        <React.Fragment key={metric.id}>
                          <TableRow
                            hover
                            sx={{
                              '&:hover': metric.source_file_id ? { bgcolor: 'action.hover' } : {},
                            }}
                            onMouseEnter={() => {
                              if (metric.source_file_id && metric.source_coordinates) {
                                setHighlightMetricId(metric.id);
                              }
                            }}
                            onMouseLeave={() => {
                              setHighlightMetricId(null);
                            }}
                          >
                            <TableCell
                              onClick={() => {
                                if (metric.source_file_id) {
                                  handleViewMetricInDocument(metric);
                                }
                              }}
                              sx={{
                                cursor: metric.source_file_id ? 'pointer' : 'default',
                              }}
                            >
                              <Typography variant="body2" fontWeight="medium">
                                {metric.metric_definitions?.name || 'Unknown'}
                              </Typography>
                            </TableCell>
                            <TableCell
                              onDoubleClick={(e) => {
                                e.stopPropagation();
                                if (submission.status !== 'completed' && (!metric.reviewed || (confidence !== null && confidence < 90))) {
                                  handleStartEdit(metric.id, metric.value);
                                }
                              }}
                              sx={{
                                cursor: submission.status !== 'completed' && (!metric.reviewed || (confidence !== null && confidence < 90)) ? 'pointer' : 'default',
                              }}
                            >
                              {editingMetricId === metric.id ? (
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                  <TextField size="small" value={editValue} onChange={(e) => setEditValue(e.target.value)} autoFocus sx={{ minWidth: 120 }} />
                                  <IconButton size="small" onClick={() => handleSaveEdit(metric.id, metric)} disabled={updateMetric.isPending} color="primary">
                                    <Check size={ICON_SIZES.sm} strokeWidth={ICON_STROKE} />
                                  </IconButton>
                                  <IconButton size="small" onClick={handleCancelEdit} disabled={updateMetric.isPending}>
                                    <XIcon size={ICON_SIZES.sm} strokeWidth={ICON_STROKE} />
                                  </IconButton>
                                </Box>
                              ) : (
                                <Typography variant="body2">{formatValue(metric.value)}</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {confidence !== null ? (
                                <Typography variant="body2" color={confidenceColor} fontWeight="medium">
                                  {confidence.toFixed(0)}%
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  -
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell onClick={(e) => e.stopPropagation()}>
                              {metric.source_file_id || metric.source_type === 'email_body' ? (
                                <Link
                                  component="button"
                                  variant="body2"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewDocument(metric.source_file_id, metric.source_type);
                                  }}
                                  sx={{ cursor: 'pointer' }}
                                >
                                  {metric.source_type}
                                </Link>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  {metric.source_type}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              {metric.reviewed ? (
                                <Chip label="Yes" size="small" color="success" />
                              ) : confidence !== null && confidence < 90 && submission.status !== 'completed' ? (
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => handleMarkReviewed(metric.id)}
                                  disabled={updateMetric.isPending}
                                  sx={{
                                    fontSize: '0.75rem',
                                    textTransform: 'none',
                                    minWidth: 'auto',
                                    px: 1,
                                  }}
                                >
                                  Mark Reviewed
                                </Button>
                              ) : (
                                <Chip label="No" size="small" color="default" />
                              )}
                            </TableCell>
                          </TableRow>

                          {/* Comment Rows */}
                          {metric.metric_comments && metric.metric_comments.length > 0 && (
                            <>
                              {metric.metric_comments
                                .filter((c: any) => !c.reply_id)
                                .map((comment: any) => {
                                  const replies = metric.metric_comments.filter((r: any) => r.reply_id === comment.id);
                                  return (
                                    <React.Fragment key={comment.id}>
                                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                                        <TableCell colSpan={5} sx={{ pl: 6, py: 1 }}>
                                          <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Typography variant="caption" fontWeight="medium" color="text.secondary">
                                              {comment.users?.full_name || comment.users?.email || 'Unknown'}:
                                            </Typography>
                                            <Typography variant="caption" color="text.primary">
                                              {comment.comment}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                                              {new Date(comment.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                            </Typography>
                                          </Box>
                                        </TableCell>
                                      </TableRow>
                                      {replies.map((reply: any) => (
                                        <TableRow key={reply.id} sx={{ bgcolor: 'grey.100' }}>
                                          <TableCell colSpan={5} sx={{ pl: 10, py: 0.5 }}>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                              <Typography variant="caption" fontWeight="medium" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                                ↳ {reply.users?.full_name || reply.users?.email || 'Unknown'}:
                                              </Typography>
                                              <Typography variant="caption" color="text.primary" sx={{ fontSize: '0.7rem' }}>
                                                {reply.comment}
                                              </Typography>
                                              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontSize: '0.7rem' }}>
                                                {new Date(reply.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                              </Typography>
                                            </Box>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </React.Fragment>
                                  );
                                })}
                            </>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {(!submission.metrics || submission.metrics.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography variant="body2" color="text.secondary">
                            No metrics found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Files (if upload source) - moved below metrics */}
              {submission.source_type === 'upload' && submission.submission_thread?.length > 0 && (
                <>
                  <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
                    Attached Files
                  </Typography>
                  <Box sx={{ mb: 3 }}>
                    {submission.submission_thread.map((file: any) => (
                      <Paper
                        key={file.id}
                        variant="outlined"
                        sx={{
                          p: 2,
                          mb: 1,
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                        onClick={() => handleViewDocument(file.id, file.source_type)}
                      >
                        <Typography variant="body2" fontWeight="medium">
                          {file.file_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {file.mime_type} • {(file.file_size / 1024).toFixed(1)} KB
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                </>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Document Viewer Modal */}
      <DocumentViewerModal
        open={viewerOpen}
        onClose={() => {
          setViewerOpen(false);
          setHighlightMetricId(null);
          setInitialPage(undefined);
        }}
        fileId={selectedFileId}
        submissionId={submissionId}
        emailBody={selectedFileId === 'email_body' ? submission?.email_body : undefined}
        highlightMetricId={highlightMetricId}
        fundId={fundId}
        initialPage={initialPage}
      />

      {/* Email Timeline Drawer */}
      <EmailTimelineDrawer submissionId={submissionId} open={timelineOpen} onClose={() => setTimelineOpen(false)} />
    </Drawer>
  );
}
